import { CloudCheck } from "lucide-react";
import type { ReactNode } from "react";
import { QueuedThumb } from "@/components/capture/QueuedThumb";
import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@/components/ui/button";
import { useUploadQueue } from "@/hooks/use-upload-queue";
import type { QueuedUpload, UploadStatus } from "@/lib/upload-queue";

const SECTIONS: { status: UploadStatus; title: string }[] = [
  { status: "waiting", title: "Waiting" },
  { status: "sending", title: "Sending" },
  { status: "failed", title: "Failed" },
  { status: "duplicate", title: "Possible duplicate" },
  { status: "sent", title: "Sent today" },
];

const clock = (epoch: number) =>
  new Date(epoch).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

function QueueRow({ item, children }: { item: QueuedUpload; children?: ReactNode }) {
  const where = [item.projectName, item.albumName].join(" · ");
  const when =
    item.status === "sent" && item.sentAt !== null
      ? `Sent ${clock(item.sentAt)}`
      : `Taken ${clock(item.createdAt)}`;
  return (
    <li data-testid="queue-row" className="flex gap-3 rounded-lg border bg-card p-3">
      <QueuedThumb item={item} className="size-14" />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="truncate font-medium">{item.itemName ?? item.fileName}</p>
        <p className="truncate text-sm text-muted-foreground">{where}</p>
        <p className="text-xs text-muted-foreground">{when}</p>
        {children}
      </div>
    </li>
  );
}

function RowDetail({ item }: { item: QueuedUpload }) {
  const { retry, keepDuplicate, discard } = useUploadQueue();
  switch (item.status) {
    case "sending":
      return (
        <div
          role="progressbar"
          aria-label={`Sending ${item.fileName}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(item.progress * 100)}
          className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted"
        >
          <div
            className="h-full rounded-full bg-[var(--brand-accent)] transition-[width]"
            style={{ width: `${Math.round(item.progress * 100)}%` }}
          />
        </div>
      );
    case "waiting":
      return item.error ? <p className="text-xs text-muted-foreground">{item.error}</p> : null;
    case "failed":
      return (
        <>
          <p className="text-sm text-destructive">{item.error ?? "Could not upload this photo"}</p>
          <div className="flex gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={() => retry(item.id)}>
              Retry
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void discard(item.id)}>
              Discard
            </Button>
          </div>
        </>
      );
    case "duplicate":
      return (
        <>
          <p className="text-sm">
            Matches {item.duplicateOf?.fileName ?? "a photo"} already in this project
          </p>
          <div className="flex gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={() => keepDuplicate(item.id)}>
              Keep
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void discard(item.id)}>
              Discard
            </Button>
          </div>
        </>
      );
    case "sent":
      return null;
  }
}

/**
 * Everything on this phone's queue, grouped by where it is. With `ids`, only those photos
 * (the computer upload dialog lists the batch it just started) and nothing when none is left.
 */
export function QueueList({ ids }: { ids?: string[] }) {
  const queue = useUploadQueue();
  const items = ids ? queue.items.filter((item) => ids.includes(item.id)) : queue.items;
  if (items.length === 0 && ids) return null;
  if (items.length === 0) {
    return (
      <EmptyState
        icon={CloudCheck}
        title="Nothing on this phone"
        description="Photos you take wait here until they upload."
        className="rounded-lg border border-dashed py-12"
      />
    );
  }

  return (
    <div className="space-y-6">
      {SECTIONS.map(({ status, title }) => {
        const rows = items.filter((item) => item.status === status);
        if (rows.length === 0) return null;
        // Newest first, so the photo just taken is at the top.
        const ordered = [...rows].reverse();
        return (
          <section key={status} className="space-y-2">
            <h2 className="text-sm font-semibold text-muted-foreground">
              {title} <span className="tabular-nums">({rows.length})</span>
            </h2>
            <ul className="space-y-2">
              {ordered.map((item) => (
                <QueueRow key={item.id} item={item}>
                  <RowDetail item={item} />
                </QueueRow>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
